import {useCallback, useMemo, useState} from 'react'
import {AutoAssignRuleType, AutoAssignWizardStep} from '@/types/auto-assign-rule'
import type {AutoAssignWizardStep as Step} from '@/types/auto-assign-rule'
import type {InitialOwnerRequest} from '@/types/owner'

// eslint-disable-next-line i18next/no-literal-string -- responsibility is a backend type code
const DEFAULT_RESPONSIBILITY = 'BUSINESS'

export interface AutoAssignWizardFormSnapshot {
    name: string
    description: string
    owners: InitialOwnerRequest[]
    priority: number
    removalGraceDays: number
    userRisk: string
    conditionExpr: string
    ruleType: AutoAssignRuleType
    profileIds: Set<string>
    functionGroupIds: Set<string>
    version: number
}

export interface UseAutoAssignWizardFormOptions {
    initialRuleType?: AutoAssignRuleType
}

function emptyOwnerRow(): InitialOwnerRequest {
    return {ownerPartyId: '', responsibility: DEFAULT_RESPONSIBILITY}
}

function emptySnapshot(initialRuleType: AutoAssignRuleType): AutoAssignWizardFormSnapshot {
    return {
        name: '',
        description: '',
        owners: [emptyOwnerRow()],
        priority: 100,
        removalGraceDays: 7,
        userRisk: '',
        conditionExpr: '',
        ruleType: initialRuleType,
        profileIds: new Set<string>(),
        functionGroupIds: new Set<string>(),
        version: 0,
    }
}

export interface AutoAssignWizardFormApi extends AutoAssignWizardFormSnapshot {
    setName(value: string): void
    setDescription(value: string): void
    setOwners(value: InitialOwnerRequest[]): void
    updateOwner(idx: number, patch: Partial<InitialOwnerRequest>): void
    addOwner(): void
    removeOwner(idx: number): void
    setPriority(value: number): void
    setRemovalGraceDays(value: number): void
    setUserRisk(value: string): void
    setConditionExpr(value: string): void
    setRuleType(type: AutoAssignRuleType): void
    addProfile(id: string): void
    removeProfile(id: string): void
    addFunctionGroup(id: string): void
    removeFunctionGroup(id: string): void
    /**
     * Replace the entire form state with the seeded snapshot — used by the
     * wizard's update-mode rule-preload effect.
     */
    seed(snapshot: Partial<AutoAssignWizardFormSnapshot>): void
    isStepValid(step: Step): boolean
}

/**
 * Wizard form state for AutoAssignRuleRequestCreateWizard.
 *
 * Centralises the per-field setters, the rule-type toggle (which clears the
 * opposite selection set), the seed-from-URL preload, and per-step
 * forward-guard validation. The page's component body delegates every state
 * read/write to this hook.
 *
 * Forward-guard rules:
 *  - {@code BASICS}: rule name set and every owner row has a partyId + responsibility.
 *  - {@code CONDITION}: condition expression non-empty (server-side validity is
 *    layered on top by the page using {@code services.autoAssignRules.validateExpression}).
 *  - {@code TEST}: always passes — the test is optional.
 *  - {@code PROFILES}: at least one profile (ACCESS_PROFILE rule type) or one
 *    function group (FUNCTION_GROUP rule type).
 *  - {@code REVIEW}: terminal step, always valid.
 */
export function useAutoAssignRuleRequestWizardForm(
    options: UseAutoAssignWizardFormOptions = {},
): AutoAssignWizardFormApi {
    const initialRuleType = options.initialRuleType ?? AutoAssignRuleType.ACCESS_PROFILE
    const [snapshot, setSnapshot] = useState<AutoAssignWizardFormSnapshot>(() => emptySnapshot(initialRuleType))

    const update = useCallback((patch: Partial<AutoAssignWizardFormSnapshot>) => {
        setSnapshot(prev => ({...prev, ...patch}))
    }, [])

    const setName = useCallback((value: string) => update({name: value}), [update])
    const setDescription = useCallback((value: string) => update({description: value}), [update])
    const setOwners = useCallback((value: InitialOwnerRequest[]) => update({owners: value}), [update])
    const updateOwner = useCallback((idx: number, patch: Partial<InitialOwnerRequest>) => {
        setSnapshot(prev => ({
            ...prev,
            owners: prev.owners.map((r, i) => (i === idx ? {...r, ...patch} : r)),
        }))
    }, [])
    const addOwner = useCallback(() => {
        setSnapshot(prev => ({...prev, owners: [...prev.owners, emptyOwnerRow()]}))
    }, [])
    const removeOwner = useCallback((idx: number) => {
        setSnapshot(prev => ({...prev, owners: prev.owners.filter((_, i) => i !== idx)}))
    }, [])
    const setPriority = useCallback((value: number) => update({priority: value}), [update])
    const setRemovalGraceDays = useCallback((value: number) => update({removalGraceDays: value}), [update])
    const setUserRisk = useCallback((value: string) => update({userRisk: value}), [update])
    const setConditionExpr = useCallback((value: string) => update({conditionExpr: value}), [update])

    const setRuleType = useCallback((type: AutoAssignRuleType) => {
        setSnapshot(prev => {
            const next: AutoAssignWizardFormSnapshot = {...prev, ruleType: type}
            if (type === AutoAssignRuleType.ACCESS_PROFILE) {
                next.functionGroupIds = new Set<string>()
            } else {
                next.profileIds = new Set<string>()
            }
            return next
        })
    }, [])

    const addProfile = useCallback((id: string) => {
        setSnapshot(prev => ({...prev, profileIds: new Set([...prev.profileIds, id])}))
    }, [])

    const removeProfile = useCallback((id: string) => {
        setSnapshot(prev => {
            const next = new Set(prev.profileIds)
            next.delete(id)
            return {...prev, profileIds: next}
        })
    }, [])

    const addFunctionGroup = useCallback((id: string) => {
        setSnapshot(prev => ({...prev, functionGroupIds: new Set([...prev.functionGroupIds, id])}))
    }, [])

    const removeFunctionGroup = useCallback((id: string) => {
        setSnapshot(prev => {
            const next = new Set(prev.functionGroupIds)
            next.delete(id)
            return {...prev, functionGroupIds: next}
        })
    }, [])

    const seed = useCallback((patch: Partial<AutoAssignWizardFormSnapshot>) => {
        setSnapshot(prev => ({...prev, ...patch}))
    }, [])

    const isStepValid = useCallback((step: Step): boolean => {
        switch (step) {
            case AutoAssignWizardStep.BASICS:
                return snapshot.name.trim().length > 0
                    && snapshot.owners.length > 0
                    && snapshot.owners.every(o => o.ownerPartyId.length > 0 && o.responsibility.length > 0)
            case AutoAssignWizardStep.CONDITION:
                return snapshot.conditionExpr.trim().length > 0
            case AutoAssignWizardStep.TEST:
                return true
            case AutoAssignWizardStep.PROFILES:
                return snapshot.ruleType === AutoAssignRuleType.FUNCTION_GROUP
                    ? snapshot.functionGroupIds.size > 0
                    : snapshot.profileIds.size > 0
            case AutoAssignWizardStep.REVIEW:
                return true
            default:
                return false
        }
    }, [snapshot])

    return useMemo(
        () => ({
            ...snapshot,
            setName,
            setDescription,
            setOwners,
            updateOwner,
            addOwner,
            removeOwner,
            setPriority,
            setRemovalGraceDays,
            setUserRisk,
            setConditionExpr,
            setRuleType,
            addProfile,
            removeProfile,
            addFunctionGroup,
            removeFunctionGroup,
            seed,
            isStepValid,
        }),
        [
            snapshot, setName, setDescription, setOwners, updateOwner, addOwner, removeOwner,
            setPriority, setRemovalGraceDays, setUserRisk, setConditionExpr, setRuleType,
            addProfile, removeProfile, addFunctionGroup, removeFunctionGroup, seed, isStepValid,
        ],
    )
}
